import React, { useRef } from 'react';
import {
  X,
  Download,
  Mail,
  Printer,
  Check,
  AlertCircle,
  Calendar,
  Building2,
  DollarSign,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

interface InvoiceGeneratorProps {
  invoice: any;
  company: any;
  onClose: () => void;
  onSendEmail?: () => void;
}

export default function InvoiceGenerator({
  invoice,
  company,
  onClose,
  onSendEmail,
}: InvoiceGeneratorProps) {
  const invoiceRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    window.print();
    toast.success('Print dialog opened');
  };

  const handleDownloadPDF = () => {
    // In production, use jsPDF or similar library
    toast.success('PDF download started', {
      description: 'Invoice will be downloaded as PDF',
    });
    
    // For now, trigger print which can save as PDF
    window.print();
  };

  const handleSendEmail = () => {
    if (onSendEmail) {
      onSendEmail();
    } else {
      toast.success('Invoice sent via email');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'sent':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'overdue':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'draft':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-100 overflow-y-auto">
      {/* Action Bar - Hidden on Print */}
      <div className="bg-white border-b shadow-sm p-4 print:hidden sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <div>
              <h2 className="font-bold text-gray-900">
                Invoice {invoice.invoiceNumber}
              </h2>
              <p className="text-sm text-gray-600">{company.name}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPDF}
              className="h-10"
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="h-10"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button
              size="sm"
              onClick={handleSendEmail}
              className="h-10 bg-blue-600 hover:bg-blue-700"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email Invoice
            </Button>
          </div>
        </div>
      </div>

      {/* Invoice Document */}
      <div className="max-w-4xl mx-auto p-8">
        <div
          ref={invoiceRef}
          className="bg-white shadow-lg rounded-lg overflow-hidden"
          style={{ minHeight: '11in' }}
        >
          {/* Invoice Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Building2 className="h-10 w-10" />
                  <div>
                    <h1 className="text-3xl font-bold">OilWell X</h1>
                    <p className="text-blue-100">Oil Well Production Management</p>
                  </div>
                </div>
                <div className="mt-4 text-sm text-blue-100">
                  <p>123 Oil Field Road</p>
                  <p>Houston, TX 77002</p>
                  <p>contact@oilwellx.com</p>
                  <p>(555) 123-4567</p>
                </div>
              </div>

              <div className="text-right">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 mb-3">
                  <p className="text-xs text-blue-100 uppercase tracking-wider mb-1">
                    Invoice
                  </p>
                  <p className="text-2xl font-bold">
                    {invoice.invoiceNumber}
                  </p>
                </div>
                <Badge
                  className={`${getStatusColor(invoice.status)} border-2 text-sm px-3 py-1`}
                >
                  {invoice.status.toUpperCase()}
                </Badge>
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="p-8">
            {/* Bill To & Invoice Info */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              {/* Bill To */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Bill To
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-bold text-gray-900 text-lg mb-1">
                    {company.name}
                  </p>
                  <p className="text-gray-700">{company.adminName}</p>
                  <p className="text-gray-700">{company.adminEmail}</p>
                  {company.location && (
                    <p className="text-gray-700 mt-2">{company.location}</p>
                  )}
                </div>
              </div>

              {/* Invoice Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Invoice Details
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Issue Date:</span>
                    <span className="font-semibold text-gray-900">
                      {formatDate(invoice.issueDate)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Due Date:</span>
                    <span className="font-semibold text-gray-900">
                      {formatDate(invoice.dueDate)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Billing Period:</span>
                    <span className="font-semibold text-gray-900">
                      {formatDate(invoice.billingPeriodStart)} -{' '}
                      {formatDate(invoice.billingPeriodEnd)}
                    </span>
                  </div>
                  {invoice.paidDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Paid Date:</span>
                      <span className="font-semibold text-green-600">
                        {formatDate(invoice.paidDate)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Line Items Table */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Services
              </h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Unit Price
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {invoice.lineItems.map((item: any, index: number) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {item.description}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right">
                          {item.quantity}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right">
                          {formatCurrency(item.unitPrice)}
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
                          {formatCurrency(item.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="flex justify-end mb-8">
              <div className="w-80">
                <div className="space-y-2">
                  <div className="flex justify-between text-gray-700">
                    <span>Subtotal:</span>
                    <span className="font-semibold">
                      {formatCurrency(invoice.subtotal)}
                    </span>
                  </div>
                  {invoice.discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount:</span>
                      <span className="font-semibold">
                        -{formatCurrency(invoice.discount)}
                      </span>
                    </div>
                  )}
                  {invoice.tax > 0 && (
                    <div className="flex justify-between text-gray-700">
                      <span>Tax:</span>
                      <span className="font-semibold">
                        {formatCurrency(invoice.tax)}
                      </span>
                    </div>
                  )}
                  <div className="border-t-2 border-gray-200 pt-2 mt-2">
                    <div className="flex justify-between text-xl font-bold text-gray-900">
                      <span>Total:</span>
                      <span>{formatCurrency(invoice.total)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Status */}
                {invoice.status === 'paid' && (
                  <div className="mt-4 bg-green-50 border-2 border-green-200 rounded-lg p-4 flex items-center justify-center space-x-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="font-semibold text-green-700">
                      PAID IN FULL
                    </span>
                  </div>
                )}

                {invoice.status === 'overdue' && (
                  <div className="mt-4 bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-center justify-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <span className="font-semibold text-red-700">
                      PAYMENT OVERDUE
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div className="mb-8 bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Notes:
                </h3>
                <p className="text-sm text-gray-700">{invoice.notes}</p>
              </div>
            )}

            {/* Payment Instructions */}
            <div className="border-t pt-8">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">
                Payment Instructions
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Bank Transfer
                  </h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p>
                      <span className="font-medium">Bank:</span> Chase Bank
                    </p>
                    <p>
                      <span className="font-medium">Account:</span> 1234567890
                    </p>
                    <p>
                      <span className="font-medium">Routing:</span> 021000021
                    </p>
                    <p>
                      <span className="font-medium">Reference:</span>{' '}
                      {invoice.invoiceNumber}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Check Payment
                  </h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p>Make checks payable to:</p>
                    <p className="font-medium">OilWell X LLC</p>
                    <p>123 Oil Field Road</p>
                    <p>Houston, TX 77002</p>
                    <p className="mt-2">
                      <span className="font-medium">Memo:</span>{' '}
                      {invoice.invoiceNumber}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t mt-8 pt-8 text-center text-sm text-gray-600">
              <p className="mb-2">
                Thank you for your business! For any questions, please contact
                us at billing@oilwellx.com
              </p>
              <p className="text-xs text-gray-500">
                OilWell X | Copyright © 2026 | Developed by{' '}
                <a
                  href="https://TGE.LA"
                  className="text-blue-600 hover:underline"
                >
                  TGE
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body {
            background: white;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
